// ** React Imports
import { useEffect, useState } from 'react';

import { client } from 'src/utils/axios-client';
import {
    LeadScreenStore,
    LeadNote,
    TaskList,
    NewTaskStore,
    CompletedTaskStore,
    TaskListDialogStore,
} from 'src/stores/LeadStore';
import { leadEndpoints, optionsEndpoints } from '../../../configs/endpoints';

// ** MUI Imports
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Avatar from '@mui/material/Avatar';
import { styled } from '@mui/material/styles';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import MuiTimeline, { TimelineProps } from '@mui/lab/Timeline';
import { timeSince } from 'src/utils/date-utils';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PerfectScrollbar from 'react-perfect-scrollbar';
import StandardSkeleton from '../../common/StandardSkeleton';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import SaveIcon from '@mui/icons-material/Save';
import { ProblemDetails } from '../../../types/ApiErrorResponse';
import Divider from '@mui/material/Divider';
import DateTimeDisplay from '../../common/DateTimeDisplay';
import LeadDataFormater from './LeadDataFormater';
import DateTimeDisplayWithLabel from '../../common/DateTimeDisplayWithLabel';
import Tooltip from '@mui/material/Tooltip';
import TaskIcon from '@mui/icons-material/Task';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { Controller } from 'react-hook-form';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { SelectOption } from 'src/types/common';
import MenuItem from '@mui/material/MenuItem';
import CustomDatePicker from '../../common/CustomDatePicker';
import CustomDatePickerNoError from '../../common/CustomDatePickerNoError';
import TaskItem from './TaskItem';
import RenderIf, { ClaimType, ClaimValue } from '../../common/RenderIf';
import SectionAppBar from '../../../v2/components/Section/SectionAppBar';
import CloseButton from '../../../v2/components/Button/CloseButton';
import StandardButton from '../../../v2/components/Button/StandardButton';

// Styled Timeline component
const Timeline = styled(MuiTimeline)<TimelineProps>(({ theme }) => ({
    margin: 0,
    padding: 0,
    marginLeft: theme.spacing(0.75),
    '& .MuiTimelineItem-root': {
        '&:before': {
            display: 'none',
        },
        '&:last-child': {
            minHeight: 60,
        },
    },
}));

type Props = {
    onClose?: () => void;
};

const TaskView = ({ onClose }: Props) => {
    const [records, setRecords] = useState([] as TaskList[]);
    const selectedLead = LeadScreenStore.useState((s) => s.selectedLead);
    const task = NewTaskStore.useState((s) => s);
    const hasChanged = CompletedTaskStore.useState((s) => s.hasChanged);

    const [hasLoaded, setHasLoaded] = useState(false);
    const [showAddTask, setShowAddTask] = useState(false);
    const [taskValue, setTaskValue] = useState('');
    const [taskAdded, setTaskAdded] = useState(false);

    const [selectedOwner, setSelectedOwner] = useState<string>('');
    const [options, setOptions] = useState([] as SelectOption[]);
    const [startDate, setStartDate] = useState<string>('');

    const addMode = TaskListDialogStore.useState((s) => s.addMode);

    useEffect(() => {
        const fetchTasks = async () => {
            setHasLoaded(false);
            const response = await client.get(leadEndpoints.gettasks, { params: { id: selectedLead?.id } });
            setRecords(response.data);
            setHasLoaded(true);
            if (addMode) {
                setTaskValue('');
                setSelectedOwner('');
                setShowAddTask(true);
                setTaskAdded(false);
            }
        };

        if (selectedLead) fetchTasks();
        if (!selectedLead) setRecords([]);
    }, [selectedLead, taskAdded, hasChanged]);

    useEffect(() => {
        const load = async () => {
            if (!selectedLead) return;
            const response = await client.get(optionsEndpoints.ownerEndpoint);
            setOptions(response.data);
        };

        load();
    }, []);

    const onAddNewTask = () => {
        setTaskValue('');
        setSelectedOwner('');
        setShowAddTask(true);
        setTaskAdded(false);
    };

    const onCancel = () => {
        setTaskValue('');
        setShowAddTask(false);
        TaskListDialogStore.update((s) => {
            s.addMode = false;
        });
    };

    const onSubmit = async () => {
        if (task.ownerId == '') return;
        if (task.details == '') return;
        NewTaskStore.update((s) => {
            if (selectedLead) s.leadId = selectedLead?.id;
        });

        await client
            .post(leadEndpoints.createnewtask, task)
            .then(async (response) => {
                setShowAddTask(false);
                setTaskAdded(true);
                TaskListDialogStore.update((s) => {
                    s.addMode = false;
                });
            })
            .catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        const errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\';
                        });
                    }
                }
            });
    };

    const markComplete = async (taskid: string) => {
        await client
            .post(leadEndpoints.marktaskascomplete.replace('{taskid}', taskid))
            .then(async (response) => {
                setShowAddTask(false);
                setTaskAdded(true);
            })
            .catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        const errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\';
                        });
                    }
                }
            });
    };

    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTaskValue(event.target.value);
        NewTaskStore.update((s) => {
            s.details = event.target.value as string;
        });
    };

    const handleOwnerChange = (event: SelectChangeEvent) => {
        setSelectedOwner(event.target.value as string);
        NewTaskStore.update((s) => {
            s.ownerId = event.target.value as string;
        });
        NewTaskStore.update((s) => {
            if (selectedLead) s.leadId = selectedLead?.id;
        });
    };

    if (hasLoaded) {
        return (
            <>
                <SectionAppBar
                    title={'Tasks'}
                    buttons={
                        <>
                            {!showAddTask && (
                                <RenderIf claimType={ClaimType.Leads} claimValue={ClaimValue.AddTasks}>
                                    <StandardButton icon={<NoteAddIcon />} onClick={onAddNewTask} color="info">
                                        New Task
                                    </StandardButton>
                                </RenderIf>
                            )}

                            <CloseButton onClick={onClose}>Close</CloseButton>
                        </>
                    }
                    subTitle=""
                />
                <Card sx={{ mb: 6 }} data-testid="activity-timeline">
                    <CardContent>
                        {showAddTask && (
                            <Grid container spacing={2} justifyContent={'right'}>
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    justifyContent="left"
                                    sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                >
                                    <FormControl>
                                        <InputLabel id="demo-simple-select-labelq">Assign To (required)</InputLabel>
                                        <Select
                                            labelId="demo-simple-select-labelq"
                                            id="demo-simple-select"
                                            value={selectedOwner}
                                            label="Assign To (required)"
                                            onChange={handleOwnerChange}
                                            size="small"
                                        >
                                            {options.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    justifyContent="left"
                                    sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                >
                                    <CustomDatePickerNoError
                                        label="Due Date (optional)"
                                        value={task.dueDate}
                                        onChange={(value) =>
                                            NewTaskStore.update((s) => {
                                                s.dueDate = value;
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={12}
                                    justifyContent="center"
                                    sx={{ display: 'flex', alignItems: 'right', flexDirection: 'column' }}
                                >
                                    <TextField
                                        id="newnote"
                                        label="Details"
                                        multiline
                                        rows={6}
                                        placeholder="Enter details here..."
                                        onChange={handleTextChange}
                                        fullWidth
                                        value={taskValue}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={8}
                                    justifyContent="center"
                                    sx={{ display: 'flex', alignItems: 'right', flexDirection: 'column' }}
                                ></Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={2}
                                    justifyContent="center"
                                    sx={{ display: 'flex', alignItems: 'right', flexDirection: 'column' }}
                                >
                                    <Button variant="contained" color="primary" size="small" onClick={onCancel}>
                                        Cancel
                                    </Button>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={2}
                                    justifyContent="center"
                                    sx={{ display: 'flex', alignItems: 'right', flexDirection: 'column' }}
                                >
                                    <Button
                                        variant="contained"
                                        color="info"
                                        startIcon={<SaveIcon />}
                                        size="small"
                                        onClick={onSubmit}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </Grid>
                        )}
                        <Timeline>
                            <PerfectScrollbar options={{ wheelPropagation: false }}>
                                <Box sx={{ height: 750 }}>
                                    {records.map((record) => (
                                        <TimelineItem key={record.id} data-testid="task">
                                            <TimelineSeparator>
                                                <TimelineDot sx={{ backgroundColor: 'green' }} />
                                                <TimelineConnector />
                                            </TimelineSeparator>
                                            <TimelineContent>
                                                <TaskItem record={record} />
                                            </TimelineContent>
                                        </TimelineItem>
                                    ))}
                                </Box>
                            </PerfectScrollbar>
                        </Timeline>
                    </CardContent>
                </Card>
            </>
        );
    } else {
        return <StandardSkeleton />;
    }
};

export { TaskView };

export default TaskView;
